import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { buildCatalog, dataTypeLabel } from './CustomFieldDefinitionModal';
import type { CustomFieldDefinition, CatalogEntry } from './CustomFieldDefinitionModal';

/**
 * ReuseCustomFieldModal — 7/28 N2: the PULL half of custom-field reuse. On a client (or
 * property) that doesn't have a field yet, browse the custom fields that ALREADY exist
 * anywhere in the org and apply one (or several) to THIS scope — without leaving the
 * profile and without recreating a duplicate. Mirrors the "Apply to other clients" PUSH:
 * each apply is POST /api/custom-fields/definitions {<scope>, label, data_type, options},
 * whose deterministic field_key makes a scope that already has the field a no-op (409 →
 * skipped). Fields already on this scope are filtered out.
 */
export interface ReuseCustomFieldModalProps {
  open: boolean;
  onClose: () => void;
  entityType: 'clients' | 'properties';
  entityId: string;
  /** field_keys already defined on this scope — never offered for reuse. */
  existingKeys: string[];
  scopeLabel: string;
  onApplied?: () => void;
}

export function ReuseCustomFieldModal({
  open, onClose, entityType, entityId, existingKeys, scopeLabel, onApplied,
}: ReuseCustomFieldModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setSelected(new Set()); setSearch(''); setBusy(false); } }, [open]);

  const catalogQuery = useQuery<CustomFieldDefinition[]>({
    queryKey: ['custom-field-catalog'],
    queryFn: async ({ signal }) => {
      const resp = await api.get<CustomFieldDefinition[] | { rows: CustomFieldDefinition[] }>(
        '/api/custom-field-definitions?limit=200', signal,
      );
      return Array.isArray(resp) ? resp : resp.rows;
    },
    enabled: open,
    staleTime: 2 * 60_000,
  });

  // The FULL reuse set (org catalog minus what this scope already has). Selection and
  // apply() resolve against THIS, not the search-narrowed view — so a field picked under
  // one search term is never silently dropped when the term changes.
  const reusable = useMemo<CatalogEntry[]>(
    () => buildCatalog(catalogQuery.data ?? []).filter((c) => !existingKeys.includes(c.field_key)),
    [catalogQuery.data, existingKeys],
  );
  // The visible list narrows the reusable set by the search box.
  const available = useMemo<CatalogEntry[]>(() => {
    const q = search.trim().toLowerCase();
    return q ? reusable.filter((c) => c.label.toLowerCase().includes(q)) : reusable;
  }, [reusable, search]);

  function toggle(key: string) {
    setSelected((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  }

  const scopeField = entityType === 'clients' ? 'client_id' : 'property_id';

  async function apply() {
    // Resolve from the FULL reusable set — NOT the search-narrowed `available` — so a
    // field selected under an earlier search term is applied, not silently dropped.
    const entries = reusable.filter((c) => selected.has(c.field_key));
    if (entries.length === 0) return;
    setBusy(true);
    let applied = 0, skipped = 0, failed = 0;
    for (const e of entries) {
      const body: Record<string, unknown> = { [scopeField]: entityId, label: e.label, data_type: e.data_type };
      if (e.data_type === 'select' && e.options) body.options = e.options;
      try { await api.post('/api/custom-fields/definitions', body); applied += 1; }
      catch (err) { if (err instanceof ApiClientError && err.status === 409) skipped += 1; else failed += 1; }
    }
    setBusy(false);
    queryClient.invalidateQueries({ queryKey: ['custom-field-definitions'] });
    queryClient.invalidateQueries({ queryKey: ['custom-fields', entityType, entityId] });
    queryClient.invalidateQueries({ queryKey: ['custom-field-catalog'] });
    if (applied > 0) {
      const bits = [`Applied ${applied} field${applied === 1 ? '' : 's'} to ${scopeLabel}`];
      if (skipped) bits.push(`${skipped} already there`);
      if (failed) bits.push(`${failed} failed`); // surface genuine (non-409) failures, don't hide them
      toast({ variant: failed ? 'info' : 'success', message: bits.join(' · ') + '.' });
      onApplied?.();
      if (failed === 0) onClose(); // keep the modal open on failures so the reviewer can retry
    } else if (failed > 0) {
      toast({ variant: 'error', message: `Couldn't apply the selected field${failed === 1 ? '' : 's'}.` });
    } else {
      onClose();
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reuse an existing field"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={apply} disabled={busy || selected.size === 0}>
            {busy ? 'Applying…' : `Apply${selected.size ? ` (${selected.size})` : ''}`}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
          Pick a custom field that already exists elsewhere to add it to {scopeLabel} — no need to recreate it.
          It keeps the same field key, so values line up across profiles.
        </p>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search fields…" />
        {catalogQuery.isLoading ? (
          <p style={{ color: 'var(--color-ink-3)' }}>Loading fields…</p>
        ) : available.length === 0 ? (
          <p style={{ color: 'var(--color-muted)' }}>
            No other fields to reuse — {scopeLabel} already has every field defined in the system.
            Use “+ Add field” to create a new one.
          </p>
        ) : (
          <div style={{
            maxHeight: 300, overflowY: 'auto', border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)', background: 'var(--color-card)', padding: 'var(--sp-2)',
            display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)',
          }}>
            {available.map((c) => {
              const checked = selected.has(c.field_key);
              return (
                <label key={c.field_key} style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
                  padding: 'var(--sp-1) var(--sp-2)', cursor: 'pointer',
                  fontSize: 'var(--fs-sm)', borderRadius: 'var(--r-sm)',
                  background: checked ? 'var(--brand-primary-bg)' : 'transparent',
                }}>
                  <input type="checkbox" checked={checked} style={{ accentColor: 'var(--brand-primary)' }} onChange={() => toggle(c.field_key)} />
                  <span style={{ flex: 1, minWidth: 0 }}>{c.label}</span>
                  <Badge variant="neutral">{dataTypeLabel[c.data_type]}</Badge>
                  {c.usedByCount > 0 && (
                    <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)', whiteSpace: 'nowrap' }}>on {c.usedByCount}</span>
                  )}
                </label>
              );
            })}
          </div>
        )}
        {selected.size > 0 && <Badge variant="info">{selected.size} selected</Badge>}
      </div>
    </Modal>
  );
}
